import {EventCard} from '@commercial-helios/ui/src/components/event-card'
import {colors, tokens} from '@commercial-helios/ui/src/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {CardsCarousel} from '@/components/cards-carousel'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {OnDemandResourcePageQuery} from '@/graphql-types'
import SignupBannerSection from '@/sections/signup-banner'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import LocalizedLink from '@helios/shared/components/localized-link'
import PrismicLink from '@helios/shared/components/prismic-link/prismic-link'
import {Main} from '@helios/shared/components/skip-to-content'
import {Container, Section} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import type {FormConfig} from '@helios/shared/types/consents'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentField from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

import {OnDemandResource} from './on-demand-resource'

type Props = PageProps<OnDemandResourcePageQuery>

const OnDemandResourcePage = ({data}: Props) => {
    const content = data.prismicOnDemandResourcePage.data

    const webinarFormConfig = getEnvironmentField(
        content.on_demand_resource_form.document.data.form_production,
        content.on_demand_resource_form.document.data.form_staging
    ) as FormConfig

    const imperativeConsents = getEnvironmentField(
        content.on_demand_resource_form.document.data.imperative_consents_production,
        content.on_demand_resource_form.document.data.imperative_consents_staging
    )

    const bannerFormId = getEnvironmentField(
        content.signup_banner.document.data.form_production.id,
        content.signup_banner.document.data.form_staging.id
    )

    const resourcePages = data.allPrismicOnDemandResourcePage.nodes.map(({data, uid}) => ({
        ...data,
        to: `/webinars/${uid}`,
    }))

    const eventData = resourcePages.map((resourcePage) => {
        return {
            title: resourcePage.title,
            description: resourcePage.description,
            category: resourcePage.category.document.data.name,
            language: resourcePage.language,
            duration: resourcePage.duration,
            Image: () => <Image image={resourcePage.title_image} />,
            CTA: (props: React.ComponentProps<typeof PrismicLink>) => (
                <LocalizedLink {...props} to={resourcePage.to}>
                    {content.watch_more_card_cta}
                </LocalizedLink>
            ),
        }
    })

    const [showCarousel, setShowCarousel] = React.useState(true)

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />
            <Main>
                <Section>
                    <FeatureBlock
                        title={content.title}
                        image={content.title_image}
                        preTitle={content.category.document.data.name}
                        body={content.description}
                    />
                </Section>

                <OnDemandResource
                    wistiaVideoId={content.wistia_id}
                    thumbnail={content.video_thumbnail}
                    formTitle={content.on_demand_resource_form.document.data.form_title}
                    formDescription={content.on_demand_resource_form.document.data.form_description}
                    submitButtonText={
                        content.on_demand_resource_form.document.data.submit_button_text
                    }
                    successMessage={content.on_demand_resource_form.document.data.success_message}
                    recurrentVisitorMessage={
                        content.on_demand_resource_form.document.data.recurrent_visitor_message
                    }
                    formConfig={webinarFormConfig}
                    imperativeConsents={imperativeConsents}
                    onRequest={() => {
                        setShowCarousel(true)
                    }}
                />

                {showCarousel && (
                    <Section edgeToEdge>
                        <WatchMoreSectionTitle>
                            <Text as="h2" variant="heading-base">
                                {content.watch_more_title}
                            </Text>
                            <ViewAllLink
                                variant="paragraph-large"
                                as={PrismicLink}
                                {...content.watch_more_top_link}
                            >
                                {content.watch_more_top_link_text}
                            </ViewAllLink>
                        </WatchMoreSectionTitle>
                        <CardsCarousel>
                            {eventData.map((event) => (
                                <StyledEventCard key={event.title} {...event} />
                            ))}
                        </CardsCarousel>
                        <MobileViewAllLink
                            variant="paragraph-large"
                            as={PrismicLink}
                            {...content.watch_more_top_link}
                        >
                            {content.watch_more_top_link_text}
                        </MobileViewAllLink>
                    </Section>
                )}

                <SignupBannerSection
                    color="pink"
                    title={content.signup_banner.document.data.title}
                    submitText={content.signup_banner.document.data.submit_text}
                    subtitle={content.signup_banner.document.data.subtitle}
                    formId={bannerFormId}
                    placeholder={content.signup_banner.document.data.email_input_placeholder}
                    errorText={content.signup_banner.document.data.email_input_error_text}
                />
            </Main>
            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<OnDemandResourcePageQuery>) => {
    const pageData = data.prismicOnDemandResourcePage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const WatchMoreSectionTitle = styled(Container)`
    display: grid;
    grid-template-columns: 1fr;
    margin-bottom: ${tokens.spacing24};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 1fr;
        margin-bottom: ${tokens.spacing40};
    }
`

const ViewAllLink = styled(TextLink)`
    display: none;

    ${mq.min('sm')} {
        display: block;
        justify-self: end;
    }
`

const MobileViewAllLink = styled(TextLink)`
    display: inline-block;
    margin-top: ${tokens.spacing20};
    margin-left: ${tokens.spacing24};

    ${mq.min('sm')} {
        display: none;
    }
`

const StyledEventCard = styled(EventCard)`
    padding: ${tokens.spacing20};
    border: 1px solid ${colors.border.dark};

    ${mq.min('sm')} {
        padding: ${tokens.spacing28};
    }
`

export const query = graphql`
    query OnDemandResourcePage(
        $uid: String!
        $locale: String!
        $imageBreakpoints: [Int]
        $threeColumns: String
    ) {
        prismicOnDemandResourcePage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            id
            uid
            lang
            type
            last_publication_date
            alternate_languages {
                uid
                lang
                type
            }
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title
                title_image {
                    alt
                    copyright
                    dimensions {
                        height
                        width
                    }
                    url
                    gatsbyImageData(
                        breakpoints: $imageBreakpoints
                        sizes: "(max-width: 1440px) 100vw, 1240px"
                    )
                }
                duration
                description
                category {
                    document {
                        ... on PrismicOnDemandResourceCategory {
                            id
                            data {
                                name
                            }
                        }
                    }
                }
                language
                wistia_id
                video_thumbnail {
                    alt
                    copyright
                    dimensions {
                        height
                        width
                    }
                    url
                    gatsbyImageData(
                        breakpoints: $imageBreakpoints
                        sizes: "(max-width: 1440px) 100vw, 1240px"
                    )
                }
                watch_more_title
                watch_more_top_link {
                    ...PrismicLink
                }
                watch_more_top_link_text
                watch_more_card_cta
                on_demand_resource_form {
                    document {
                        ... on PrismicOnDemandResourceForm {
                            data {
                                form_title
                                form_description
                                submit_button_text
                                success_message
                                recurrent_visitor_message
                                form_production {
                                    formId
                                    consents {
                                        type
                                        communicationConsents {
                                            id
                                            required
                                        }
                                    }
                                }
                                form_staging {
                                    formId
                                    consents {
                                        type
                                        communicationConsents {
                                            id
                                            required
                                        }
                                    }
                                }
                                imperative_consents_production {
                                    consent {
                                        document {
                                            ...ConsentWebsite
                                        }
                                    }
                                }
                                imperative_consents_staging {
                                    consent {
                                        document {
                                            ...ConsentWebsite
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                signup_banner {
                    document {
                        ... on PrismicSignupBanner {
                            id
                            data {
                                title
                                subtitle
                                submit_text
                                email_input_placeholder
                                email_input_error_text
                                form_production {
                                    id
                                }
                                form_staging {
                                    id
                                }
                            }
                        }
                    }
                }
            }
        }
        allPrismicOnDemandResourcePage(limit: 3, filter: {uid: {ne: $uid}}) {
            nodes {
                _previewable
                uid
                lang
                type
                last_publication_date
                data {
                    title
                    title_image {
                        alt
                        copyright
                        dimensions {
                            height
                            width
                        }
                        url
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                    duration
                    description
                    category {
                        document {
                            ... on PrismicOnDemandResourceCategory {
                                id
                                data {
                                    name
                                }
                            }
                        }
                    }
                    language
                }
            }
        }
    }
`

export default withPrismicPreview(OnDemandResourcePage)
